import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, Link } from 'react-router-dom'
import {
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  TrashIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline'
import { api } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'


interface Conversation {
  id: string
  sessionId: string
  startedAt: string
  lastMessageAt: string
  endedAt?: string
  isActive: boolean
  messageCount: number
  duration: number
  ipAddress: string
  userAgent: string
  userInfo?: {
    name?: string
    email?: string
    phone?: string
  }
  source: string
  deviceType: string
  firstMessage: string
}


const Conversations: React.FC = () => {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [selectedConversations, setSelectedConversations] = useState<string[]>([])
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Fetch conversations with real-time updates
  const { data: conversationsData, isLoading, error } = useQuery({
    queryKey: ['conversations', page, search, status],
    queryFn: () => api.get('/conversations', {
      params: { page, limit: 20, search, status }
    }).then(res => res.data),
    refetchInterval: 3000, // Refetch every 3 seconds for real-time updates
  })


  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/conversations/${id}`),
    onSuccess: () => {
      toast.success('Conversation deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete conversation')
    }
  })

  // Bulk operations mutation
  const bulkMutation = useMutation({
    mutationFn: ({ action, conversationIds }: { action: string, conversationIds: string[] }) =>
      api.post('/conversations/bulk', { action, conversationIds }),
    onSuccess: (data) => {
      toast.success(data.data.message)
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      setSelectedConversations([])
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Bulk operation failed')
    }
  })

  // Status update mutation
  const statusMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string, isActive: boolean }) =>
      api.patch(`/conversations/${id}/status`, { isActive }),
    onSuccess: () => {
      toast.success('Status updated successfully')
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      queryClient.invalidateQueries({ queryKey: ['conversation'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update status')
    }
  })

  const handleSelectAll = () => {
    if (selectedConversations.length === conversationsData?.conversations?.length) {
      setSelectedConversations([])
    } else {
      setSelectedConversations(conversationsData?.conversations?.map((c: Conversation) => c.id) || [])
    }
  }

  const handleSelectConversation = (id: string) => {
    setSelectedConversations(prev =>
      prev.includes(id)
        ? prev.filter(cId => cId !== id)
        : [...prev, id]
    )
  }

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m`
    const hours = Math.floor(minutes / 60)
    return `${hours}h ${minutes % 60}m`
  }

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType?.toLowerCase()) {
      case 'mobile': return <DevicePhoneMobileIcon className="h-4 w-4" />
      case 'desktop': return <ComputerDesktopIcon className="h-4 w-4" />
      default: return <GlobeAltIcon className="h-4 w-4" />
    }
  }

  const handleExport = () => {
    window.open('/api/conversations/export/csv', '_blank')
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center text-red-600">
          Error loading conversations: {(error as any).response?.data?.error || 'Unknown error'}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <ChatBubbleLeftRightIcon className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Conversations</h1>
            <p className="text-sm text-muted-foreground">
              {conversationsData?.pagination?.total || 0} total conversations
            </p>
          </div>
        </div>
        <button
          onClick={handleExport}
          className="btn-secondary flex items-center space-x-2"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
              <input
                type="text"
                placeholder="Search conversations..."
                className="pl-10 input-field"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-4 w-4 text-muted-foreground/70" />
              <select
                className="input-field"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {/* Conversations List */}
        <div className="space-y-4">
          {/* Bulk Actions */}
          {selectedConversations.length > 0 && (
            <div className="card">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {selectedConversations.length} selected
                </span>
                <div className="flex space-x-2">
                  <button
                    onClick={() => bulkMutation.mutate({ action: 'activate', conversationIds: selectedConversations })}
                    className="btn-sm btn-success"
                    disabled={bulkMutation.isPending}
                  >
                    Activate
                  </button>
                  <button
                    onClick={() => bulkMutation.mutate({ action: 'deactivate', conversationIds: selectedConversations })}
                    className="btn-sm btn-secondary"
                    disabled={bulkMutation.isPending}
                  >
                    Deactivate
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Are you sure you want to delete selected conversations?')) {
                        bulkMutation.mutate({ action: 'delete', conversationIds: selectedConversations })
                      }
                    }}
                    className="btn-sm btn-danger"
                    disabled={bulkMutation.isPending}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Conversations Table */}
          <div className="card overflow-hidden">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-border">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          <input
                            type="checkbox"
                            checked={selectedConversations.length === conversationsData?.conversations?.length && conversationsData?.conversations?.length > 0}
                            onChange={handleSelectAll}
                            className="rounded border-input"
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Conversation
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Messages
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Duration
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                      {conversationsData?.conversations?.map((conversation: Conversation) => (
                        <tr 
                          key={conversation.id}
                          className="hover:bg-muted/50"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedConversations.includes(conversation.id)}
                              onChange={() => handleSelectConversation(conversation.id)}
                              className="rounded border-input"
                            />
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0 relative">
                                {getDeviceIcon(conversation.deviceType)}
                                {conversation.isActive && (
                                  <div className="absolute -top-1 -right-1 h-3 w-3 bg-green-500 rounded-full animate-pulse" title="Active conversation" />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium truncate flex items-center space-x-2">
                                  {/* The eye icon is a small target for the
                                      primary action on the row, so the name is
                                      a link to the same place. */}
                                  <Link
                                    to={`/conversations/${conversation.id}`}
                                    className="text-foreground underline-offset-4 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
                                  >
                                    {conversation.userInfo?.name || `${conversation.sessionId.substring(0, 8)}...`}
                                  </Link>
                                  {conversation.isActive && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      Live
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-muted-foreground truncate max-w-xs">
                                  {conversation.userInfo?.email && (
                                    <span className="block text-xs text-muted-foreground/70">{conversation.userInfo.email}</span>
                                  )}
                                  {conversation.firstMessage}
                                </div>
                                <div className="text-xs text-muted-foreground/70 mt-1">
                                  {new Date(conversation.startedAt).toLocaleString()}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-foreground">{conversation.messageCount}</div>
                            <div className="text-xs text-muted-foreground">{conversation.source}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-1">
                              <ClockIcon className="h-4 w-4 text-muted-foreground/70" />
                              <span className="text-sm text-foreground">
                                {formatDuration(conversation.duration)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => statusMutation.mutate({ 
                                id: conversation.id, 
                                isActive: !conversation.isActive 
                              })}
                              disabled={statusMutation.isPending}
                              className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                                conversation.isActive 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-muted text-foreground'
                              }`}
                            >
                              {conversation.isActive ? (
                                <>
                                  <CheckIcon className="h-3 w-3" />
                                  <span>Active</span>
                                </>
                              ) : (
                                <>
                                  <XMarkIcon className="h-3 w-3" />
                                  <span>Inactive</span>
                                </>
                              )}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => navigate(`/conversations/${conversation.id}`)}
                                className="text-primary hover:text-primary"
                                title="View Details"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => {
                                  if (confirm('Are you sure you want to delete this conversation?')) {
                                    deleteMutation.mutate(conversation.id)
                                  }
                                }}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                                disabled={deleteMutation.isPending}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {conversationsData?.pagination && (
                  <div className="bg-card px-4 py-3 flex items-center justify-between border-t border-border sm:px-6">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => setPage(page - 1)}
                        disabled={!conversationsData.pagination.hasPrev}
                        className="relative inline-flex items-center px-4 py-2 border border-input text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted/50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPage(page + 1)}
                        disabled={!conversationsData.pagination.hasNext}
                        className="ml-3 relative inline-flex items-center px-4 py-2 border border-input text-sm font-medium rounded-md text-foreground bg-card hover:bg-muted/50"
                      >
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-foreground">
                          Showing{' '}
                          <span className="font-medium">
                            {((conversationsData.pagination.page - 1) * conversationsData.pagination.limit) + 1}
                          </span>{' '}
                          to{' '}
                          <span className="font-medium">
                            {Math.min(conversationsData.pagination.page * conversationsData.pagination.limit, conversationsData.pagination.total)}
                          </span>{' '}
                          of{' '}
                          <span className="font-medium">{conversationsData.pagination.total}</span>{' '}
                          results
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                          <button
                            onClick={() => setPage(page - 1)}
                            disabled={!conversationsData.pagination.hasPrev}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-input bg-card text-sm font-medium text-muted-foreground hover:bg-muted/50"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => setPage(page + 1)}
                            disabled={!conversationsData.pagination.hasNext}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-input bg-card text-sm font-medium text-muted-foreground hover:bg-muted/50"
                          >
                            Next
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Conversations